/**
 * Janko Piano - Main Entry Point
 * Orchestrates all modules and initializes the application
 * @version 0.2.63
 */

import { CONFIG } from './config.js';
import { initAudio, getCurrentInstrument, setInstrument, activePointers } from './audio.js';
import { initRenderer, generateKeys, requestRender, keys } from './renderer.js';
import { 
    showToast,
    setQuickSplit,
    setQuickKeyShape,
    setQuickRows,
    setQuickChord,
    closeModalOnClick,
    initSliderInteraction
} from './ui.js';
import { setupKeyboardListeners, setupPointerListeners } from './input.js';
import { initMinimap, updateMinimap } from './minimap.js';
import { autoDetectDevice, applyMaxRowsLimit } from './device.js';
import { loadSettings, applySettings } from './settings.js';
import * as MIDI from './midi.js';
import * as Recorder from './recorder.js';

// =============================================================================
// INITIALIZATION
// =============================================================================

function init() {
    try {
        console.log('Starting Initialization...');

        // Initialize renderer first
        initRenderer();

        // Auto-detect device
        autoDetectDevice(updateMinimap);

        // Generate initial keys
        generateKeys(updateMinimap);

        // Initial render
        requestRender(getCurrentInstrument());

        // Setup input handlers
        setupKeyboardListeners(updateMinimap);
        setupPointerListeners(updateMinimap);

        // Initialize minimap
        initMinimap(updateMinimap);

        // Initialize audio (async, shows loading overlay)
        initAudio();
        
        // Initialize MIDI (async)
        MIDI.initMIDI().then(success => {
            if (success) {
                console.log('[Main] MIDI initialized successfully');
            }
        });

        // Load user settings
        const savedSettings = loadSettings();
        applySettings(savedSettings, updateMinimap);

        // Load saved recording from localStorage (silent = true to suppress toast on startup)
        Recorder.loadRecording(true);

        // Initialize slider interaction (hide settings during drag)
        initSliderInteraction();

        // Initialize ads if available
        if (typeof window.initAds === 'function') {
            window.initAds();
        }

        // Restore fullscreen state on native platforms
        const isNative = window.Capacitor && window.Capacitor.isNativePlatform();
        if (isNative) {
            const savedFullscreen = localStorage.getItem('fullscreenState');
            if (savedFullscreen === 'true') {
                const StatusBar = window.Capacitor?.Plugins?.StatusBar;
                if (StatusBar) {
                    StatusBar.hide().catch(err => console.error('Failed to restore fullscreen:', err));
                    CONFIG.isFullscreen = true;
                }
            }
        }

        console.log('Initialization Complete.');
    } catch (err) {
        console.error('Init Error:', err);
        alert('App Init Failed: ' + err.message);
    }
}

// =============================================================================
// WINDOW LOAD HANDLER
// =============================================================================

window.addEventListener('load', () => {
    // Load saved settings from localStorage
    const savedSettings = loadSettings();
    applySettings(savedSettings, updateMinimap);
    
    // Apply maxRows limit to UI (slider and buttons)
    applyMaxRowsLimit();
    
    // Force UI refresh with loaded or default values
    setQuickSplit(CONFIG.splitOctave, updateMinimap);
    setQuickKeyShape(CONFIG.keyShape, updateMinimap);
    setQuickRows(CONFIG.rows, updateMinimap);
    setQuickChord(CONFIG.chordMode, true); // silent = true to suppress toast on init
    
    // Apply saved instrument if available
    if (savedSettings.instrument) {
        // Will be applied after audio initialization
        window.addEventListener('audio-initialized', () => {
            setInstrument(savedSettings.instrument);
        }, { once: true });
    }

    // Set dynamic version
    const verSpan = document.getElementById('app-version-span');
    if (verSpan) verSpan.textContent = 'v' + CONFIG.appVersion;

    // Restore checkbox states and hide modals if checked
    const hideInfo = localStorage.getItem('hideInfoOnStartup');
    const infoCheckbox = document.getElementById('dont-show-info-again');
    if (infoCheckbox) infoCheckbox.checked = (hideInfo === 'true');
    if (hideInfo === 'true') {
        const infoModal = document.getElementById('info-modal');
        if (infoModal) infoModal.classList.remove('visible');
    }
    
    const hideQuickSettings = localStorage.getItem('hideQuickSettingsOnStartup');
    const quickSettingsCheckbox = document.getElementById('dont-show-quicksettings-again');
    const quickSettingsModal = document.getElementById('quick-settings-modal');
    
    if (quickSettingsCheckbox) quickSettingsCheckbox.checked = (hideQuickSettings === 'true');
    
    // Show quick settings by default, hide only if user has checked the box
    if (quickSettingsModal) {
        if (hideQuickSettings === 'true') {
            quickSettingsModal.classList.remove('visible');
        } else {
            quickSettingsModal.classList.add('visible');
        }
    }

    // Initialize ads
    if (window.initAds) window.initAds();
});

window.addEventListener('resize', () => {
    // Re-detect device on resize (orientation change)
    if (typeof autoDetectDevice === 'function') {
        autoDetectDevice(updateMinimap);
    }
    if (window.updateAdLayout) window.updateAdLayout();
    generateKeys(updateMinimap);
    requestRender(getCurrentInstrument());
});

// Fullscreen change handler
document.addEventListener('fullscreenchange', () => {
    setTimeout(() => {
        generateKeys(updateMinimap);
        requestRender(getCurrentInstrument());
    }, 200);
});

// Modal click-outside handler
document.addEventListener('click', closeModalOnClick);

// =============================================================================
// EXPOSE GLOBALS FOR HTML ONCLICK HANDLERS
// =============================================================================

// Re-export functions that HTML onclick handlers need
import {
    toggleSettings,
    openSettings,
    closeSettings,
    toggleInfo,
    openQuickSettings,
    closeQuickSettings,
    openInstrumentGrid,
    closeInstrumentGrid,
    setInst,
    cycleInst,
    setChord,
    setRows,
    setWidth,
    setKeyShape,
    setColorScheme,
    setReverb,
    setChorus,
    setDistortion,
    setEcho,
    setScroll,
    toggleSplitOctave,
    toggleKeyboardLayout,
    toggleFullscreen,
    togglePayStatus,
    simulateDevice,
    applySplitOctave,
    buyPremium,
    restorePurchase,
    toggleOrientationLock
} from './ui.js';

import { toggleSustainLock } from './audio.js';

// Expose to window for HTML onclick handlers
window.toggleSettings = toggleSettings;
window.openSettings = openSettings;
window.closeSettings = closeSettings;
window.toggleInfo = toggleInfo;
window.openQuickSettings = openQuickSettings;
window.closeQuickSettings = closeQuickSettings;
window.openInstrumentGrid = openInstrumentGrid;
window.closeInstrumentGrid = closeInstrumentGrid;
window.setInst = (id) => setInst(id, updateMinimap);
window.cycleInst = (d) => cycleInst(d, updateMinimap);
window.setChord = setChord;
window.setRows = (v) => setRows(v, updateMinimap);
window.setWidth = (v) => setWidth(v, updateMinimap);
window.setKeyShape = (v) => setKeyShape(v, updateMinimap);
window.setColorScheme = (v) => setColorScheme(v, updateMinimap);
window.setReverb = setReverb;
window.setChorus = setChorus;
window.setDistortion = setDistortion;
window.setEcho = setEcho;
window.setScroll = (v) => setScroll(v, updateMinimap);
window.toggleSplitOctave = () => toggleSplitOctave(updateMinimap);
window.toggleKeyboardLayout = toggleKeyboardLayout;
window.toggleFullscreen = () => toggleFullscreen(updateMinimap);
window.togglePayStatus = togglePayStatus;
window.simulateDevice = (type) => simulateDevice(type, () => autoDetectDevice(updateMinimap), updateMinimap);
window.toggleSustainLock = toggleSustainLock;
window.showToast = showToast;
window.setQuickSplit = (v) => setQuickSplit(v, updateMinimap);
window.setQuickKeyShape = (v) => setQuickKeyShape(v, updateMinimap);
window.setQuickRows = (v) => setQuickRows(v, updateMinimap);
window.setQuickChord = setQuickChord;
window.applySplitOctave = (v) => applySplitOctave(v, updateMinimap);
window.closeModalOnClick = closeModalOnClick;
window.buyPremium = buyPremium;
window.restorePurchase = restorePurchase;
window.toggleOrientationLock = toggleOrientationLock;

// MIDI functions
window.sendMIDINoteOn = MIDI.sendMIDINoteOn;
window.sendMIDINoteOff = MIDI.sendMIDINoteOff;
window.sendMIDIControlChange = MIDI.sendMIDIControlChange;
window.selectMIDIInput = (deviceId) => {
    if (deviceId) {
        MIDI.enableMIDIInput(deviceId);
    } else {
        MIDI.disableMIDIInput();
    }
};
window.selectMIDIOutput = (deviceId) => {
    if (deviceId) {
        MIDI.enableMIDIOutput(deviceId);
    } else {
        MIDI.disableMIDIOutput();
    }
};
window.refreshMIDIDevices = MIDI.refreshDevices;
window.getInputDevices = MIDI.getInputDevices;
window.getOutputDevices = MIDI.getOutputDevices;
window.getSelectedInputDevice = MIDI.getSelectedInputDevice;
window.getSelectedOutputDevice = MIDI.getSelectedOutputDevice;

// Recorder functions
window.toggleRecording = Recorder.toggleRecording;
window.togglePlayback = Recorder.togglePlayback;
window.recordMIDIEvent = Recorder.recordEvent;
window.clearRecording = Recorder.clearRecording;
window.getIsRecording = Recorder.getIsRecording;
window.getIsPlaying = Recorder.getIsPlaying;
window.saveRecording = Recorder.saveRecording;
window.loadRecording = Recorder.loadRecording;
window.getRecordingSize = Recorder.getRecordingSize;
window.exportMIDI = Recorder.exportMIDI;
window.importMIDI = Recorder.importMIDI;
window.openMIDIFile = Recorder.openMIDIFile;
window.openNativeFileBrowser = Recorder.openNativeFileBrowser;

// MIDI File Picker Modal Functions
window.showMIDIFilePicker = (files, onSelectCallback) => {
    console.log('[Main] showMIDIFilePicker called with', files.length, 'files');
    
    const modal = document.getElementById('midi-file-picker-modal');
    const fileList = document.getElementById('midi-file-list');
    
    if (!modal) {
        console.error('[Main] midi-file-picker-modal element not found!');
        return;
    }
    
    if (!fileList) {
        console.error('[Main] midi-file-list element not found!');
        return;
    }
    
    // Clear previous list
    fileList.innerHTML = '';
    
    // Create file list items
    files.forEach((file, index) => {
        const fileItem = document.createElement('div');
        fileItem.style.cssText = 'padding: 12px; margin: 5px 0; background: rgba(255,255,255,0.05); border-radius: 8px; transition: background 0.2s; border: 1px solid rgba(255,255,255,0.1); display: flex; align-items: center; gap: 10px;';
        
        // Extract timestamp from filename if present (janko_YYYYMMDDTHHMMSS.mid)
        const match = file.name.match(/janko_(\d{8}T\d{6})/);
        let dateStr = '';
        if (match) {
            const timestamp = match[1];
            const year = timestamp.substr(0, 4);
            const month = timestamp.substr(4, 2);
            const day = timestamp.substr(6, 2);
            const hour = timestamp.substr(9, 2);
            const minute = timestamp.substr(11, 2);
            dateStr = `${year}-${month}-${day} ${hour}:${minute}`;
        }
        
        // File info section (clickable)
        const fileInfo = document.createElement('div');
        fileInfo.style.cssText = 'flex: 1; cursor: pointer;';
        fileInfo.innerHTML = `
            <div style="font-weight: bold; margin-bottom: 4px;">${file.name}</div>
            ${dateStr ? `<div style="font-size: 11px; color: #888;">${dateStr}</div>` : ''}
        `;
        fileInfo.onclick = () => {
            console.log('[Main] File selected:', file.name);
            closeMIDIFilePicker();
            onSelectCallback(file.name);
        };
        
        // Share button
        const shareBtn = document.createElement('button');
        shareBtn.style.cssText = 'padding: 8px; background: rgba(255,255,255,0.15); border: none; border-radius: 6px; cursor: pointer; transition: background 0.2s; color: rgba(255,255,255,0.9);';
        shareBtn.innerHTML = `
            <svg viewBox="0 0 24 24" width="20" height="20" style="display: block;">
                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" fill="currentColor"/>
            </svg>
        `;
        shareBtn.onmouseover = () => shareBtn.style.background = 'rgba(255,255,255,0.3)';
        shareBtn.onmouseout = () => shareBtn.style.background = 'rgba(255,255,255,0.15)';
        shareBtn.onclick = (e) => {
            e.stopPropagation();
            console.log('[Main] Share clicked:', file.name);
            if (window.shareMIDIFile) {
                window.shareMIDIFile(file.name);
            }
        };
        
        // Delete button
        const deleteBtn = document.createElement('button');
        deleteBtn.style.cssText = 'padding: 8px; background: rgba(255,100,100,0.3); border: none; border-radius: 6px; cursor: pointer; transition: background 0.2s; color: rgba(255,200,200,0.95);';
        deleteBtn.innerHTML = `
            <svg viewBox="0 0 24 24" width="20" height="20" style="display: block;">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
            </svg>
        `;
        deleteBtn.onmouseover = () => deleteBtn.style.background = 'rgba(255,100,100,0.5)';
        deleteBtn.onmouseout = () => deleteBtn.style.background = 'rgba(255,100,100,0.3)';
        deleteBtn.onclick = (e) => {
            e.stopPropagation();
            console.log('[Main] Delete clicked:', file.name);
            // Ask for confirmation
            if (confirm(`Delete "${file.name}"?\n\nThis action cannot be undone.`)) {
                if (window.deleteMIDIFile) {
                    window.deleteMIDIFile(file.name);
                }
            }
        };
        
        // Hover effect for entire item
        fileItem.onmouseover = () => fileItem.style.background = 'rgba(255,255,255,0.1)';
        fileItem.onmouseout = () => fileItem.style.background = 'rgba(255,255,255,0.05)';
        
        fileItem.appendChild(fileInfo);
        fileItem.appendChild(shareBtn);
        fileItem.appendChild(deleteBtn);
        fileList.appendChild(fileItem);
    });
    
    console.log('[Main] Added', files.length, 'file items to list');
    console.log('[Main] Showing modal...');
    
    // Show modal
    modal.classList.add('visible');
    
    console.log('[Main] Modal classes:', modal.className);
};

window.closeMIDIFilePicker = () => {
    const modal = document.getElementById('midi-file-picker-modal');
    modal.classList.remove('visible');
};

// MIDI visual feedback functions
window.highlightMIDIKey = (midiNote) => {
    // Find the key with this MIDI note
    const key = keys.find(k => k.midi === midiNote);
    if (key) {
        // Create a unique pointer ID for MIDI input
        const midiPointerId = `midi_${midiNote}`;
        
        // Add to activePointers to trigger visual highlight
        if (!activePointers.has(midiPointerId)) {
            activePointers.set(midiPointerId, [{
                midi: midiNote,
                channel: 0,
                keyId: key.id,
                isRoot: true,
                startTime: Date.now()
            }]);
            requestRender(getCurrentInstrument());
        }
    }
};

window.unhighlightMIDIKey = (midiNote) => {
    const midiPointerId = `midi_${midiNote}`;
    if (activePointers.has(midiPointerId)) {
        activePointers.delete(midiPointerId);
        requestRender(getCurrentInstrument());
    }
};

// Expose CONFIG globally
window.CONFIG = CONFIG;

// =============================================================================
// START APPLICATION
// =============================================================================

init();
